<template>
  <div class="admin-login-page">
    <section class="admin-login-panel">
      <div class="brand-block">
        <img :src="branding.logo" :alt="branding.logoAlt" class="brand-logo">
        <div>
          <div class="brand-name">LearningFirst Admin</div>
          <div class="brand-subtitle">Management console</div>
        </div>
      </div>

      <div class="code-hint">
        A one-time login code will be sent to the admin mailbox
        <span class="code-hint-email">{{ sentTo || maskedAdminEmail }}</span>.
        You can also log in with the admin password.
      </div>

      <el-form ref="form" :model="form" :rules="rules" @submit.native.prevent>
        <el-form-item prop="admin_code">
          <el-input
            v-model="form.admin_code"
            type="password"
            placeholder="Login code or admin password"
            autocomplete="current-password"
            @keyup.enter.native="submit"
          >
            <el-button
              slot="suffix"
              class="send-code-button"
              :disabled="countdown > 0 || sending"
              :loading="sending"
              @click="sendCode"
            >{{ countdown > 0 ? `${countdown}s` : 'Send Code' }}</el-button>
          </el-input>
        </el-form-item>
        <el-button type="primary" class="login-button" :loading="loading" @click="submit">
          Log in
        </el-button>
      </el-form>
    </section>
  </div>
</template>

<script>
import branding from '@/config/branding'
import { learningFirstAdminLogin, learningFirstAdminSendLoginCode } from '@/apis/lfAdmin'
import { getLfAdminRedirect, setLfAdminSession } from '@/utils/lfAdminSession'

export default {
  name: 'AdminLogin',
  data() {
    return {
      branding,
      loading: false,
      sending: false,
      countdown: 0,
      countdownTimer: null,
      sentTo: '',
      form: {
        admin_code: ''
      },
      rules: {
        admin_code: [
          { required: true, whitespace: true, message: 'Please enter the login code or admin password', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    maskedAdminEmail() {
      return 'co*****@learningfirst.io'
    }
  },
  beforeDestroy() {
    if (this.countdownTimer) clearInterval(this.countdownTimer)
  },
  methods: {
    startCountdown(seconds) {
      this.countdown = seconds
      if (this.countdownTimer) clearInterval(this.countdownTimer)
      this.countdownTimer = setInterval(() => {
        this.countdown -= 1
        if (this.countdown <= 0) {
          clearInterval(this.countdownTimer)
          this.countdownTimer = null
        }
      }, 1000)
    },
    async sendCode() {
      this.sending = true
      try {
        const res = await learningFirstAdminSendLoginCode()
        const data = res.data
        if (data && data.code === 0) {
          this.sentTo = (data.data && data.data.sent_to) || this.maskedAdminEmail
          this.$message.success(`Login code sent to ${this.sentTo}`)
          this.startCountdown(60)
        } else if (data && Number(data.code) === -429) {
          const cooldown = data.data && data.data.cooldown ? Math.ceil(data.data.cooldown) : 60
          this.$message.warning(`Code was sent recently, please retry in ${cooldown}s`)
          this.startCountdown(Math.min(cooldown, 120))
        } else {
          this.$message.error((data && data.msg) || 'Failed to send login code')
        }
      } catch (error) {
        this.$message.error('Failed to send login code')
        console.error(error)
      } finally {
        this.sending = false
      }
    },
    submit() {
      this.$refs.form.validate(async (valid) => {
        if (!valid) return
        this.loading = true
        try {
          const res = await learningFirstAdminLogin(this.form.admin_code)
          if (res.data && res.data.code === 0) {
            setLfAdminSession(res.data.data || {})
            this.$message.success('Admin login successful')
            this.$router.replace(getLfAdminRedirect(this.$route.query.redirect))
          } else {
            this.$message.error((res.data && res.data.msg) || 'Login failed')
          }
        } catch (error) {
          this.$message.error('Login failed')
          console.error(error)
        } finally {
          this.loading = false
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
.admin-login-page {
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    linear-gradient(135deg, rgba(17, 89, 175, 0.08), rgba(103, 194, 58, 0.07)),
    #f5f7fb;
  padding: 32px 16px;
  box-sizing: border-box;
}

.admin-login-panel {
  width: 100%;
  max-width: 380px;
  background: #ffffff;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  box-shadow: 0 18px 50px rgba(31, 45, 61, 0.12);
  padding: 32px;
  box-sizing: border-box;
}

.brand-block {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 28px;
}

.brand-logo {
  width: 42px;
  height: 42px;
  object-fit: contain;
}

.brand-name {
  color: #1f2d3d;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
}

.brand-subtitle {
  color: #909399;
  font-size: 13px;
  margin-top: 4px;
}

.code-hint {
  color: #606266;
  font-size: 13px;
  line-height: 1.6;
  background: #f5f7fa;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 20px;
}

.code-hint-email {
  color: #1159af;
  font-weight: 600;
}

.send-code-button {
  padding: 9px 12px;
  font-size: 13px;
}

.login-button {
  width: 100%;
}
</style>
